import React, {
  useContext,
  useState,
  useCallback,
  PropsWithChildren,
} from 'react';
import { FollowsContext } from '../context/FollowsContext';
import { UserDataContext } from '../context/UserDataContext';
import { supabase } from '../supabaseClient';
import { useSupabaseListener } from '../hooks/useSupabaseListener';
import { FollowData } from '../model/types';

export type { FollowData as Follow };

export const FollowsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { userRecord } = useContext(UserDataContext);
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);

  const fetchFollows = useCallback(async (): Promise<FollowData[]> => {
    if (!userRecord) return [];
    const { data, error: fetchError } = await supabase
      .from('follows')
      .select('*')
      .eq('user_record_id', userRecord.id)
      .order('created_at', { ascending: false });
    if (fetchError) {
      console.error('FollowsProvider fetch error:', fetchError.message);
      return [];
    }
    return (data ?? []) as FollowData[];
  }, [userRecord]);

  const { data: follows, isLoading } = useSupabaseListener<FollowData>(
    {
      table: 'follows',
      filter: userRecord ? `user_record_id=eq.${userRecord.id}` : undefined,
      fetchInitial: fetchFollows,
      disabled: !userRecord,
    },
    [userRecord?.id],
  );

  const getFollows = useCallback(() => follows, [follows]);

  return (
    <FollowsContext.Provider
      value={{
        follows,
        getFollows,
        isLoading,
        error,
        clearError,
      }}
    >
      {children}
    </FollowsContext.Provider>
  );
};
